import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import TipTapEditor from '@/components/editor/TipTapEditor'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useSync } from '@/hooks/useSync'
import { useComments } from '@/hooks/useComments'
import CommentSidebar from '@/components/comments/CommentSidebar'
import {
  Trash2, History, User, Clock,
  Plus, X, Check, Loader2, AlertCircle, MoreHorizontal,
  FileText, Copy
} from 'lucide-react'

interface NoteData {
  id: string
  title: string
  body_markdown: string
  slug: string
  directory: string
  tags: string[]
  file_path: string
  updated_at: string
  content_hash: string
  parent_id: string | null
  has_children: boolean
  breadcrumbs: { title: string; slug: string }[]
}

interface RevisionData {
  id: string
  user_name: string
  change_type: string
  title_before: string | null
  title_after: string | null
  created_at: string
}

interface ChildNote {
  id: string
  title: string
  slug: string
  excerpt: string | null
  has_children: boolean
  updated_at: string
}

interface Props {
  note: NoteData
  children: ChildNote[]
  all_tags: string[]
  revisions: RevisionData[]
}

type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error' | 'unchanged'

function timeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return date.toLocaleDateString()
}

function changeTypeLabel(type: string): { text: string; className: string } {
  switch (type) {
    case 'create':
      return { text: 'Created', className: 'text-emerald-600' }
    case 'update':
      return { text: 'Updated', className: 'text-blue-600' }
    case 'delete':
      return { text: 'Deleted', className: 'text-red-600' }
    default:
      return { text: type, className: 'text-gray-500' }
  }
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}

export default function NotesShow({ note, children, all_tags, revisions }: Props) {
  useSync('notes')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
  const [showTagInput, setShowTagInput] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [tags, setTags] = useState(note.tags)
  const [statusVisible, setStatusVisible] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null)

  const commentsHook = useComments(note.slug)

  // Determine which comments are detached (anchor_text not found in current body)
  const detachedIds = useMemo(() => {
    const ids = new Set<string>()
    const body = note.body_markdown || ''
    for (const c of commentsHook.comments) {
      if (!c.resolved && !body.includes(c.anchor_text)) {
        ids.add(c.id)
      }
    }
    return ids
  }, [commentsHook.comments, note.body_markdown])

  const titleRef = useRef(note.title)
  const bodyRef = useRef(note.body_markdown)
  const tagsRef = useRef(note.tags)
  const dirtyRef = useRef(false)
  const lastSavedTitleRef = useRef(note.title)
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Reset all local state when navigating between notes (same component reused by React)
  useEffect(() => {
    titleRef.current = note.title
    bodyRef.current = note.body_markdown
    tagsRef.current = note.tags
    lastSavedTitleRef.current = note.title
    dirtyRef.current = false
    setTags(note.tags)
    setSaveStatus('saved')
    setStatusVisible(false)
    setShowTagInput(false)
    setTagInput('')
    setDeleteOpen(false)
    setHistoryOpen(false)
    // Reset uncontrolled textarea value since defaultValue doesn't update on re-render
    if (titleInputRef.current) {
      titleInputRef.current.value = note.title
    }
  }, [note.slug])

  const resizeTitle = useCallback(() => {
    const el = titleInputRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [])

  useEffect(() => {
    resizeTitle()
    // Auto-focus and select title for new "Untitled" pages
    if (note.title === 'Untitled' && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [resizeTitle, note.slug])

  const flashStatus = useCallback((status: SaveStatus) => {
    setSaveStatus(status)
    setStatusVisible(true)
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current)
    if (status === 'saved' || status === 'unchanged') {
      statusTimeoutRef.current = setTimeout(() => setStatusVisible(false), 2000)
    }
  }, [])

  const performAutosave = useCallback(async () => {
    if (!dirtyRef.current) return

    flashStatus('saving')

    try {
      const response = await fetch(`/notes/${note.slug}/autosave`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          note: {
            title: titleRef.current,
            body_markdown: bodyRef.current,
            tags: tagsRef.current,
          },
        }),
      })

      await response.json()

      if (response.ok) {
        dirtyRef.current = false
        flashStatus('saved')
        // Refresh sidebar if title changed so tree updates instantly
        if (titleRef.current !== lastSavedTitleRef.current) {
          lastSavedTitleRef.current = titleRef.current
          router.reload({ only: ['sidebar'], preserveScroll: true })
        }
      } else {
        flashStatus('error')
      }
    } catch {
      flashStatus('error')
    }
  }, [note.slug, flashStatus])

  const debouncedAutosave = useDebouncedCallback(performAutosave, 2000)

  const markDirty = useCallback(() => {
    dirtyRef.current = true
    flashStatus('unsaved')
    debouncedAutosave()
  }, [debouncedAutosave, flashStatus])

  const handleManualSave = useCallback(() => {
    debouncedAutosave.cancel()

    router.patch(`/notes/${note.slug}`, {
      note: {
        title: titleRef.current,
        body_markdown: bodyRef.current,
        tags: tagsRef.current,
      },
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        dirtyRef.current = false
        flashStatus('saved')
      },
      onError: () => {
        flashStatus('error')
      },
    })
  }, [note.slug, debouncedAutosave, flashStatus])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  function handleTitleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    titleRef.current = e.target.value
    resizeTitle()
    markDirty()
  }

  function handleTitleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const prosemirror = document.querySelector('.ProseMirror') as HTMLElement
      prosemirror?.focus()
    }
  }

  function handleBodyChange(markdown: string) {
    bodyRef.current = markdown
    markDirty()
  }

  function addTag(tag: string) {
    const trimmed = tag.trim().toLowerCase()
    if (trimmed && !tagsRef.current.includes(trimmed)) {
      const updated = [...tagsRef.current, trimmed]
      tagsRef.current = updated
      setTags(updated)
      markDirty()
    }
    setTagInput('')
    setShowTagInput(false)
  }

  function removeTag(tag: string) {
    const updated = tagsRef.current.filter(t => t !== tag)
    tagsRef.current = updated
    setTags(updated)
    markDirty()
  }

  function handleDelete() {
    setDeleteOpen(false)
    router.delete(`/notes/${note.slug}`)
  }

  function copyLink() {
    navigator.clipboard.writeText(window.location.href)
  }

  const statusConfig = {
    saved: { icon: <Check className="h-3 w-3" />, text: 'Saved' },
    saving: { icon: <Loader2 className="h-3 w-3 animate-spin" />, text: 'Saving...' },
    unsaved: { icon: null, text: 'Editing' },
    error: { icon: <AlertCircle className="h-3 w-3" />, text: 'Save failed' },
    unchanged: { icon: <Check className="h-3 w-3" />, text: 'Saved' },
  }

  const status = statusConfig[saveStatus]

  return (
    <Layout>
      <div className="relative min-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <button
              onClick={() => router.visit('/notes')}
              className="hover:text-gray-600 transition-colors"
            >
              Pages
            </button>
            {note.breadcrumbs && note.breadcrumbs.length > 0 && (
              <>
                {note.breadcrumbs.map((crumb) => (
                  <span key={crumb.slug} className="flex items-center gap-1.5">
                    <span className="text-gray-300">/</span>
                    <button
                      onClick={() => router.visit(`/notes/${crumb.slug}`)}
                      className="hover:text-gray-600 transition-colors"
                    >
                      {crumb.title}
                    </button>
                  </span>
                ))}
              </>
            )}
            <span className="text-gray-300">/</span>
            <span className="text-gray-500 truncate max-w-xs">{titleRef.current || 'Untitled'}</span>

            <span
              className={`ml-3 flex items-center gap-1 transition-opacity duration-300 ${
                statusVisible ? 'opacity-100' : 'opacity-0'
              } ${saveStatus === 'error' ? 'text-red-500' : 'text-gray-400'}`}
            >
              {status.icon}
              {status.text}
            </span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-gray-400 hover:text-gray-600"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={copyLink}>
                <Copy className="mr-2 h-3.5 w-3.5" />
                Copy link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setHistoryOpen(!historyOpen)}>
                <History className="mr-2 h-3.5 w-3.5" />
                History ({revisions.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigator.clipboard.writeText(bodyRef.current)}>
                <FileText className="mr-2 h-3.5 w-3.5" />
                Copy as Markdown
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteOpen(true)}
                className="text-red-500 focus:text-red-500"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete page
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <textarea
          ref={titleInputRef}
          defaultValue={note.title}
          onChange={handleTitleInput}
          onKeyDown={handleTitleKeyDown}
          placeholder="Untitled"
          rows={1}
          className="w-full resize-none bg-transparent text-[40px] font-bold leading-tight text-gray-900 outline-none border-none placeholder:text-gray-300 mb-1"
        />

        <div className="flex items-center gap-1.5 mb-8">
          {tags.map(tag => (
            <span
              key={tag}
              className="group inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}

          {showTagInput ? (
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault()
                  addTag(tagInput)
                }
                if (e.key === 'Escape') {
                  setShowTagInput(false)
                  setTagInput('')
                }
              }}
              onBlur={() => {
                if (tagInput.trim()) addTag(tagInput)
                else setShowTagInput(false)
              }}
              placeholder="Add tag..."
              list="tag-suggestions"
              className="w-24 rounded bg-white px-2 py-0.5 text-xs text-gray-700 outline-none placeholder:text-gray-400 border border-gray-200 focus:border-gray-400"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setShowTagInput(true)}
              className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:text-gray-500 transition-colors"
            >
              <Plus className="h-3 w-3" />
            </button>
          )}

          <datalist id="tag-suggestions">
            {all_tags.filter(t => !tags.includes(t)).map(t => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>

        {children && children.length > 0 && (
          <div className="mb-8 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {children.length} sub-page{children.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-1">
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => router.visit(`/notes/${child.slug}`)}
                  className="flex items-center gap-3 w-full rounded px-3 py-2 text-left hover:bg-gray-100 transition-colors group"
                >
                  <FileText className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-500 shrink-0" />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 truncate">
                    {child.title}
                  </span>
                  {child.excerpt && (
                    <span className="text-xs text-gray-400 truncate hidden sm:block">
                      {child.excerpt}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                    {timeAgo(child.updated_at)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <TipTapEditor
          key={note.slug}
          content={note.body_markdown}
          onChange={handleBodyChange}
          onSave={handleManualSave}
          borderless
          showBubbleMenu
          showSlashCommands
          placeholder="Type '/' for commands, or just start writing..."
          noteSlug={note.slug}
          comments={commentsHook.comments}
          onAddComment={commentsHook.addComment}
          onCommentClick={(commentId) => {
            setActiveCommentId(commentId)
            setSidebarOpen(true)
            const el = document.getElementById(`comment-thread-${commentId}`)
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }}
        />

        {historyOpen && revisions.length > 0 && (
          <div className="mt-16 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <History className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                History
              </span>
              <button
                onClick={() => setHistoryOpen(false)}
                className="ml-auto text-xs text-gray-400 hover:text-gray-500 transition-colors"
              >
                Close
              </button>
            </div>
            <div className="space-y-1">
              {revisions.map((rev) => {
                const label = changeTypeLabel(rev.change_type)
                return (
                  <div
                    key={rev.id}
                    className="flex items-center gap-3 rounded px-3 py-2 text-xs hover:bg-gray-50 transition-colors"
                  >
                    <span className={`font-medium ${label.className}`}>
                      {label.text}
                    </span>
                    {rev.change_type === 'update' && rev.title_before && rev.title_after && rev.title_before !== rev.title_after && (
                      <span className="text-gray-400">
                        {rev.title_before} &rarr; {rev.title_after}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-gray-400">
                      <User className="h-2.5 w-2.5" />
                      {rev.user_name}
                    </span>
                    <span className="flex items-center gap-1 text-gray-400 ml-auto">
                      <Clock className="h-2.5 w-2.5" />
                      {timeAgo(rev.created_at)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this page?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">{titleRef.current}</span> will be moved to trash.
              It can be recovered from git history.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <CommentSidebar
        comments={commentsHook.comments}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        activeCommentId={activeCommentId}
        detachedIds={detachedIds}
        onReply={commentsHook.replyToComment}
        onResolve={commentsHook.resolveComment}
        onUnresolve={commentsHook.unresolveComment}
        onDelete={commentsHook.deleteComment}
        onThreadClick={(commentId) => {
          setActiveCommentId(commentId)
          // Scroll editor to the comment highlight
          const span = document.querySelector(`[data-comment-id="${commentId}"]`) as HTMLElement
          if (span) {
            span.scrollIntoView({ behavior: 'smooth', block: 'center' })
            span.classList.add('active')
            setTimeout(() => span.classList.remove('active'), 1500)
          }
        }}
      />
    </Layout>
  )
}
